import {
    Autocomplete, FormControl, FormControlLabel, FormGroup, FormHelperText,
    InputLabel, List, ListItem, ListItemText, MenuItem, Select, Switch,
    TextField, Typography, Box, CircularProgress, Chip, Tooltip
} from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import React, { useState, useMemo } from "react";
import api from "../services/api";
import { useFormError } from "./Datatable/FormErrorProvider";


/**
 * Seleção de Pools de IP por lista de switches (1 switch por pool).
 *
 * Antes era um Autocomplete multi — exigia abrir dropdown, filtrar, etc.
 * Lista de switches é mais direta: ver todos os pools de uma vez e
 * ativar/desativar individualmente.
 *
 * Props:
 *   value     Array<{description: string}>   pools selecionados
 *   handler   (newValue: Array) => void      callback ao mudar
 *   disabled  boolean
 */
export default function IpPoolSelect({ handler, value = [], disabled = false, ...props }) {
    const [groups, setGroups] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [filter, setFilter] = useState("");
    const error = useFormError();

    React.useLayoutEffect(() => {
        api.get('/access/groupsip/all').then(res => {
            setGroups(res.data || []);
        }).finally(() => setLoaded(true));
    }, []);

    const selectedSet = useMemo(
        () => new Set((value || []).map((v) => v.description)),
        [value]
    );

    const toggle = (pool) => {
        if (disabled) return;
        const isOn = selectedSet.has(pool.description);
        const next = isOn
            ? (value || []).filter((v) => v.description !== pool.description)
            : [...(value || []), pool];
        handler(next);
    };

    const filteredGroups = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return groups;
        return groups.filter((g) => (g.description || '').toLowerCase().includes(q));
    }, [groups, filter]);

    const errored = error.verify('groupip');
    const helper = error.msgError('groupip');

    return (
        <FormControl error={errored} fullWidth {...props}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Pools de IP
                </Typography>
                <Chip
                    size="small"
                    label={`${selectedSet.size} selecionado(s)`}
                    color={selectedSet.size > 0 ? 'primary' : 'default'}
                    variant="outlined"
                />
            </Box>

            <TextField
                size="small"
                placeholder="Filtrar pools..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                disabled={disabled || groups.length <= 5}
                sx={{ mb: 1 }}
            />

            <Box
                sx={{
                    maxHeight: 280,
                    overflow: 'auto',
                    border: '1px solid',
                    borderColor: errored ? 'error.main' : 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.default',
                }}
            >
                {!loaded ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={20} />
                    </Box>
                ) : filteredGroups.length === 0 ? (
                    <Typography variant="body2" sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
                        {groups.length === 0 ? 'Nenhum pool cadastrado.' : 'Nenhum pool corresponde ao filtro.'}
                    </Typography>
                ) : (
                    <FormGroup>
                        {filteredGroups.map((pool) => {
                            const checked = selectedSet.has(pool.description);
                            const ipsList = (pool.ips || '').split(',').map(s => s.trim()).filter(Boolean);
                            const tooltipContent = ipsList.length === 0
                                ? 'Nenhum IP cadastrado neste pool'
                                : (
                                    <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                            {ipsList.length} IP/range(s):
                                        </Typography>
                                        {ipsList.slice(0, 12).map((ip, i) => (
                                            <Typography key={i} variant="caption" sx={{ display: 'block', fontFamily: 'monospace' }}>
                                                {ip}
                                            </Typography>
                                        ))}
                                        {ipsList.length > 12 && (
                                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                                ... e mais {ipsList.length - 12}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            return (
                                <Box
                                    key={pool.description}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        px: 1.5,
                                        py: 0.5,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        '&:last-child': { borderBottom: 'none' },
                                        '&:hover': { bgcolor: 'action.hover' },
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                size="small"
                                                checked={checked}
                                                onChange={() => toggle(pool)}
                                                disabled={disabled}
                                                color="success"
                                            />
                                        }
                                        label={pool.description}
                                        sx={{ m: 0, flexGrow: 1 }}
                                    />
                                    <Chip
                                        size="small"
                                        label={`${ipsList.length} IP${ipsList.length === 1 ? '' : 's'}`}
                                        variant="outlined"
                                        sx={{ ml: 1 }}
                                    />
                                    <Tooltip title={tooltipContent} placement="left" arrow>
                                        <InfoOutlinedIcon
                                            fontSize="small"
                                            sx={{ ml: 1, color: 'text.secondary', cursor: 'help' }}
                                        />
                                    </Tooltip>
                                </Box>
                            );
                        })}
                    </FormGroup>
                )}
            </Box>
            {helper && <FormHelperText>{helper}</FormHelperText>}
        </FormControl>
    );
}
